// Resolver slots: QUAL variante serve esta pessoa, e que conteúdo atende essa variante.
//
// Sem DOM, sem WebSocket, sem `window`. Roda em NAVEGADOR e em SERVIDOR (Node, Deno, Bun, edge
// runtime) porque só depende de `fetch`, que é injetável. Trocar de runtime = trocar o `fetch`, não
// este arquivo — a mesma costura que o `transport.js` já usa para a fila de eventos.
//
// ## Por que o servidor é o lugar certo para quem tem servidor
//
// Medido em 21/08/2026, no site de um tenant, com tudo quente:
//
//     pelo NAVEGADOR .. o conteúdo chega 576 ms DEPOIS de o padrão já estar na tela
//     pelo SERVIDOR ... a ida custa ~120 ms somados ao TTFB, e o HTML sai PRONTO
//
// E o navegador não tem conserto por transporte: a janela entre o script poder rodar (753 ms) e a
// página pintar (839 ms) é de 86 ms, e uma ida até a região custa ~590 ms — sete vezes a janela.
// Decompondo: 401 dos 419 ms são a perna da BORDA até a região (o estático cacheado no PoP responde
// em 18 ms). O que falta é distância, não protocolo.
//
// Três transportes já testados no navegador: WebSocket (872 ms), HTTP sem preconnect, HTTP com
// preconnect (593 ms). O melhor não chega perto. Por isso: servidor quando houver servidor,
// navegador quando não houver — e agora com o custo conhecido em vez de suposto.
//
// ## O que este módulo NÃO faz
//
//     decidiu e há conteúdo ....... devolve
//     decidiu e não há conteúdo ... NÃO devolve
//     não decidiu ................. NÃO devolve
//     não deu para perguntar ...... NÃO devolve
//
// Nunca inventa um padrão. Quem chama já tem o conteúdo que a página mostraria de qualquer jeito —
// devolver um "default" daqui faria a superfície achar que a plataforma escolheu, e ninguém
// escolheu. Ausência é `{}`, e `{}` significa "siga com o que você já tinha".

/** Prazo padrão. Decisão no caminho de render tem PRAZO: passar do prazo é pior que não decidir. */
export const PRAZO_PADRAO_MS = 1000;

/**
 * Resolve os slots de uma superfície.
 *
 * @param {object} o
 * @param {string} o.baseUrl      raiz da API
 * @param {string} o.sdkToken     o token (X-SYNQ-TOKEN). No navegador ele é público por desenho —
 *                                o que o protege é a ORIGEM declarada nele, conferida no servidor.
 * @param {string} o.userId       quem está vendo
 * @param {string[]} o.slots      os lugares nomeados da superfície
 * @param {string} [o.page]       o caminho da página atual, para o conteúdo que declara em QUAIS
 *                                páginas vale. Quem não informa não recebe esse conteúdo — não
 *                                saber onde se está não é estar no lugar certo.
 * @param {number} [o.timeoutMs]  prazo
 * @param {function} [o.fetch]    injetável: é isto que torna o módulo agnóstico de runtime
 * @returns {Promise<Record<string, {content: object, style: object, variant: string, rule: string,
 *                                   segment: string}>>}  `{}` = siga com o que já tinha
 */
export async function resolveSlots(opcoes) {
  return (await resolverComDesfecho(opcoes)).slots;
}

/**
 * O mesmo resolve, dizendo TAMBÉM se a plataforma respondeu.
 *
 * `resolveSlots` colapsa quatro situações em `{}`, e para quem só vai RENDERIZAR isso está certo:
 * as quatro significam "siga com o que você já tinha". Mas duas delas são opostas para quem GUARDA
 * uma decisão:
 *
 *     a plataforma respondeu e não há nada para esta pessoa  ->  o guardado tem que ser APAGADO
 *     não deu para perguntar (rede, prazo, 500)              ->  o guardado tem que ser MANTIDO
 *
 * Colapsar as duas faria um opt-out nunca chegar (se a gente mantivesse sempre) ou um blink de wifi
 * apagar uma decisão válida (se a gente apagasse sempre). Por isso o desfecho sobe.
 *
 * `respondeu: true` exige resposta ok E um `slots` legível: corpo que não dá para entender é "não
 * sei", nunca "não há nada para você".
 *
 * @returns {Promise<{respondeu: boolean, slots: object}>}
 */
export async function resolverComDesfecho({ baseUrl, sdkToken, userId, slots, page,
                                            timeoutMs = PRAZO_PADRAO_MS, fetch: fetchImpl } = {}) {
  const nada = { respondeu: false, slots: {} };
  const nomes = (slots || []).map((s) => String(s || "").trim()).filter(Boolean);
  if (!baseUrl || !sdkToken || !userId || nomes.length === 0) return nada;

  const f = fetchImpl || (typeof fetch === "function" ? fetch : null);
  if (!f) return nada;                     // runtime sem fetch: ausência, não erro

  const url = `${String(baseUrl).replace(/\/$/, "")}/connector/personalization/resolve`;
  const ctrl = typeof AbortController === "function" ? new AbortController() : null;
  const prazo = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  try {
    const r = await f(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-SYNQ-TOKEN": sdkToken },
      body: JSON.stringify({ user_id: userId, slots: nomes,
                             ...(page ? { page: String(page) } : {}) }),
      signal: ctrl ? ctrl.signal : undefined,
    });
    if (!r || !r.ok) return nada;
    const d = await r.json();
    if (!d || typeof d.slots !== "object" || d.slots === null) return nada;
    return { respondeu: true, slots: d.slots };
  } catch {
    // Prazo estourado, rede caída, resposta ilegível: tudo é "não sei". Quem chama segue com o
    // conteúdo que já tinha, e a página não quebra por causa disto.
    return nada;
  } finally {
    if (prazo) clearTimeout(prazo);
  }
}

/**
 * O TEXTO de um slot resolvido, ou `null`.
 *
 * O conteúdo vem de fontes diferentes (preset, template, publicado à mão) e cada uma nomeia o texto
 * do seu jeito. Um lugar que sabe os três nomes evita cada superfície inventar o seu.
 */
export function textoDoSlot(resolvido) {
  const c = (resolvido && resolvido.content) || {};
  return c.title || c.text || c.body || null;
}

/**
 * Aplica os slots resolvidos com a função que a superfície souber usar.
 *
 * Existe para que quem renderiza não precise repetir a mesma decisão de "tem texto? então troca":
 * o navegador troca `textContent`, o servidor interpola no template, um app nativo chama outra
 * coisa. Devolve o que FOI aplicado — só isso deve virar impressão.
 *
 * O registro devolvido carrega o PORQUÊ, não só o quê: `rule` e `segment` são a trilha "por que esta
 * pessoa viu isto", e `decision_ts` é quando a decisão foi tomada — que pode ser bem antes de ela ter
 * sido mostrada, quando vem guardada do navegador. Sem esses campos aqui, quem for ecoar a impressão
 * teria que reabrir o mapa resolvido para achá-los, e na prática ecoaria só o par (slot, variante):
 * a decisão viraria opaca exatamente no evento que existe para explicá-la.
 *
 * `context` é o EIXO DO BRAÇO que a política declarou (`{key, value}`), e ele existe pelo mesmo
 * motivo dos outros: quem ecoa a impressão precisa levá-lo, senão o bandit lê `_all` e todos os
 * braços colapsam num contexto só. Ausente quando a plataforma não declarou eixo — e aí nada muda.
 *
 * @param {object} resolvidos  o retorno de `resolveSlots`
 * @param {(slot: string, texto: string, dado: object) => boolean} aplicar
 * @returns {Array<{slot, variant, rule, segment, context, decision_ts}>}  só o que FOI aplicado
 */
export function aplicarSlots(resolvidos, aplicar) {
  const feitos = [];
  for (const [slot, dado] of Object.entries(resolvidos || {})) {
    const texto = textoDoSlot(dado);
    if (!texto) continue;                  // conteúdo sem texto não substitui nada
    if (aplicar(slot, texto, dado) !== false) {
      const v = dado.validity || {};
      feitos.push({
        slot,
        variant: dado.variant,
        rule: dado.rule,
        segment: dado.segment,
        context: dado.context || null,
        // Ausente quando a política não declara prazo: aí a decisão não viaja e é sempre de agora.
        decision_ts: typeof v.decision_ts === "number" ? v.decision_ts : null,
      });
    }
  }
  return feitos;
}
